// ...existing code...
import React, { useState, useEffect } from "react";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { VscVerified, VscVerifiedFilled } from "react-icons/vsc";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const BASE_URL = API_BASE_URL;
const itemsToShow = 4;

export default function TopMarketPlace() {
  const [sellers, setSellers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSellers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token =
          sessionStorage.getItem("accessToken") ||
          localStorage.getItem("accessToken");

        if (!token) {
          setError("Authentication token missing. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/seller/get-sellers`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: token,
            },
            body: JSON.stringify({
              filters: {},
              page: 1,
              limit: 10,
            }),
          }
        );

        const data = await response.json();

        if (
          response.ok &&
          data.success &&
          Array.isArray(data.result?.sellers)
        ) {
          setSellers(data.result.sellers);
        } else {
          setError(data?.message || "Failed to load sellers.");
        }
      } catch (err) {
        console.error("Error fetching sellers:", err);
        setError("Failed to load sellers.");
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, []);

  const nextSlide = () =>
    setCurrent((prev) => (prev + 1) % Math.max(sellers.length, 1));
  const prevSlide = () =>
    setCurrent(
      (prev) =>
        (prev - 1 + Math.max(sellers.length, 1)) % Math.max(sellers.length, 1)
    );

  const visibleSellers = sellers.length
    ? sellers
        .slice(current, current + itemsToShow)
        .concat(
          current + itemsToShow > sellers.length
            ? sellers.slice(0, (current + itemsToShow) % sellers.length)
            : []
        )
    : [];

  const openSellerShop = (seller) => {
    if (!seller) return;
    // navigate to existing protected buyer route /shpebuye with sellerId query param
    navigate(`/shopbuyeformarketplace?sellerId=${seller.id}`);
  };

  if (loading) return <div className="p-4">Loading top marketplaces...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!sellers.length) return <div className="p-4">No sellers available.</div>;

  return (
    <div className="mt-10 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold border-b-2 border-green-500 flex items-center gap-3">
          Top Marketplaces
          <button
            onClick={() => navigate('/myorder')}
            title="My Orders"
            className="ml-2 p-1 rounded-full text-green-700 hover:bg-green-100 transition"
          >
            <AiOutlineShoppingCart className="text-xl" />
          </button>
        </h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/allsellers")}
            className="border-2 text-md border-green-300 px-3 py-2 bg-green-100 rounded-full"
          >
            View All
          </button>

          <button
            onClick={prevSlide}
            className="border-2 border-green-300 px-3 py-2 bg-green-100 rounded-full"
          >
            <MdArrowBackIosNew />
          </button>
          <button
            onClick={nextSlide}
            className="border-2 border-green-300 px-3 py-2 bg-green-100 rounded-full"
          >
            <MdArrowForwardIos />
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div className="flex gap-4 overflow-hidden mb-5">
        {visibleSellers.map((seller, i) => (
          <div
            key={seller.id || i}
            className="flex-shrink-0 h-[250px] w-[calc(25%-12px)] shadow rounded-lg border cursor-pointer"
            onClick={() => openSellerShop(seller)}
          >
            <div>
              <div className="h-[140px] relative group">
                <img
                  src={seller.cover_image?.url || "/image/eyephone.png"}
                  alt={seller.store_name || seller.name}
                  className="w-full h-40 object-cover rounded-lg group-hover:opacity-80 transition"
                />
                <p className="absolute top-3 right-4 py-1 font-medium text-[12px] px-3 rounded-xl bg-[#F9ED33] text-gray-400">
                  Popular
                </p>
                <div className=" absolute left-14  -bottom-4  z-10 text-[8px] bg-[#1ee28a] rounded-full w-[2] h-[3] p-[1px]">
                  <VscVerifiedFilled className="text-white text-[14px] " />
                </div>

                <div className="absolute left-3 -bottom-14 flex gap-3 bg-opacity-70 p-2 rounded">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#1DE78B] ">
                    <img
                      src={seller.profile_image?.url || require("../../assets/profile.png")}
                      alt={seller.user?.first_name || seller.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="relative top-6">
                    <p className="text-[14px] font-semibold">
                      {seller.name || seller.name}
                    </p>
                    <p className="text-[#929292] text-[10px]">
                      @{seller.store_name || seller.name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-14 flex justify-between items-center p-2">
                <div className="flex items-center flex-1 min-w-0">
                  {/* ✅ Dynamic product preview images */}
                  <div className="flex -space-x-3 flex-shrink-0">
                    {seller.product_images &&
                    seller.product_images.length > 0 ? (
                      seller.product_images
                        .slice(0, 4)
                        .map((img, index) => (
                          <img
                            key={index}
                            src={img.url}
                            alt={`Product ${index + 1}`}
                            className="w-8 h-8 rounded-full border-2 border-white shadow-md object-cover"
                          />
                        ))
                    ) : (
                      <>
                        <img
                          src="/image/indidvilesell.png"
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                        />
                        <img
                          src="/image/profilepic.png"
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                        />
                        <img
                          src="/image/sharebuttoncontainer.png"
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                        />
                        <img
                          src="/image/PostInputImage.png"
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                        />
                      </>
                    )}
                  </div>

                  {/* ✅ Total products info */}
                  <div className="ml-2 text-green-800 font-semibold text-[12px] truncate max-w-[120px] sm:max-w-[160px] md:max-w-[200px]">
                    {seller.total_products
                      ? `${seller.total_products} Products`
                      : "No Products"}
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openSellerShop(seller);
                  }}
                  className="border-2 border-green-300 p-2 bg-green-100 rounded-full flex-shrink-0"
                >
                  <MdArrowForwardIos />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex space-x-2">
        {sellers.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`h-2 w-2 rounded-full focus:outline-none transition-colors ${
              current === idx ? "bg-green-600 w-[20px]" : "bg-gray-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
// ...existing code...
